import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { requestCheckout } from "@/lib/open-checkout";
import { GridPattern } from "@/components/ui/grid-pattern";
import { DsIcon } from "@/components/sections/ds-icons";

/**
 * "All Attendees Receive" — value-prop section for the Claude Cowork
 * workshop detail page. On mobile: a short full-bleed image banner with
 * the content stacked below it. On desktop: a full-width, section-dominant
 * video with the value card overlaid on the right. See the desktop block
 * below for the "ethereal" treatment — it's a direct port of the fade
 * technique in the homepage hero (client/src/components/hero.tsx,
 * HoverBackground + GridPattern), applied to a charcoal stage instead of
 * the hero's cream one.
 *
 * Layout variants, selected via the `variant` prop:
 *   card-light   cream card (shipped; the only one currently wired up)
 *   card-dark    charcoal card
 *   card-two-col cream card, 6 items in two columns (3 + 3)
 *   card-art     card-two-col's card over a large painting (the `art` prop)
 *                that dissolves into the band; no grid, no video
 *
 * (Two earlier variants — a feathered gradient-mask panel and a
 * gradient-overlay-on-image treatment — were removed 2026-07-03: both
 * only existed to carry a gradient, which the design system bans, and
 * neither shipped. Don't reintroduce gradient-dependent variants here.)
 *
 * IMAGE: /images/cowork-workshop/bot-home-hero.webp is the APPROVED brand
 * imagery library asset 9de9768d-311d-405c-b4f8-a2d6c434c4d0, placed via
 * asset-promote (responsive avif/webp variants live alongside it). Used
 * as the mobile banner and as the desktop video's poster frame.
 */

const BG_IMAGE = "/images/cowork-workshop/bot-home-hero.webp";

/* Ethereal fade-to-charcoal mask for the desktop hero video — same
   radial-gradient masking technique as HoverBackground in
   client/src/components/hero.tsx. The ellipse size (radius, as a % of the
   box) has to stay under 50% on each axis, or the fade never actually
   completes before it hits the box edge. Opaque is held out to 80% of the
   radius with a short tail (80% -> 100%), so the video reads as opaque
   across nearly the full frame and the dissolve compresses into a thin
   band right at the edges, not something eating a big chunk of the scene.

   NOTE on the video's own transform: scale(0.7) with transform-origin
   LEFT CENTER (applied where this mask is used) — mask math runs against
   the video's own untransformed box, then the transform shrinks that
   entire result toward the left edge, so the video stays FLUSH with the
   section's true left edge and all of the scale-down's margin lands on
   the right, underneath/behind the card (which sits over plain charcoal
   there anyway). This was rev'd 2026-07-03: the earlier centered
   scale(0.75) inset the video ~12.5% from the left too, which read as a
   band of dead charcoal before the scene even started.

   The second mask layer (left-to-right linear gradient, intersected via
   the same mask-composite pattern the old PANEL_FADE_MASK used) is now a
   SHORT feather: fully opaque by 10% of the width instead of the old 44%.
   It exists only so the video doesn't hard-cut at the viewport edge —
   the left side of the scene should actually be visible. Right of 10%
   it contributes nothing; the right edge is governed by the ellipse
   alone.

   The third layer is the top/bottom vignette (added 2026-07-03): the
   ellipse's vertical radius is so tall that its fade tail lands past the
   box edge, leaving the top and bottom edges nearly hard cuts. This
   vertical linear gradient feathers both — opaque through the middle
   ~82%, dissolving into the charcoal over the outer ~9% at each end. */
const VIDEO_FADE_MASK = [
  "radial-gradient(ellipse 50% 62% at 50% 48%, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 82%, rgba(0,0,0,0.4) 93%, transparent 100%)",
  "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 3%, rgba(0,0,0,0.8) 6%, black 10%, black 100%)",
  "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 4%, black 9%, black 91%, rgba(0,0,0,0.55) 96%, transparent 100%)",
].join(", ");

export type AttendeesReceiveVariant = "card-light" | "card-dark" | "card-two-col" | "card-art";

/** Painting behind the card for the `card-art` variant. `base` is the
 *  responsive image base (expects -480/-800/-1200/-1600 .avif and .webp). */
export interface AttendeesArt {
  base: string;
  alt?: string;
  /** Museum plaque over the painting (Isaac, 2026-09-09: "an interesting, more
   *  artistic frame in the bottom left-hand corner, almost like a plaque for
   *  that artwork"). Three fields rather than one credit line because a plaque
   *  sets each of them differently — mono eyebrow, display title, mono year —
   *  and a single node would have to be re-split to do that. Supply any of
   *  them and the plaque renders; supply none and the painting carries no
   *  caption, which is what every page but Sales does today (Sales is the only
   *  caller of `art` at all). */
  artist?: string;
  title?: string;
  year?: string;
  /** object-position for the painting, default "left center". */
  position?: string;
  /** Intrinsic size of the -1600 variant, so the box is reserved before the
   *  bytes arrive (CLS). Defaults to the Grainstack plate's 1600 x 956. */
  width?: number;
  height?: number;
  /** Desktop treatment of the painting. `"ground"` (default): the sharp plate
   *  on the left, fading into the band's own colour, the way every caller has
   *  had it. `"blur"` (Isaac, 2026-09-17, Sales): a blurred copy of the same
   *  painting fills the WHOLE band, edge to edge, and the sharp plate lies on
   *  it, so the picture runs sharp on the left into soft on the right with a
   *  long, gradual fade and never into a flat colour. Isaac's words: "The left
   *  side should be no blur. The right side is blurred, but we still see the
   *  image. The transition to blur is gradual and smooth." */
  tail?: "ground" | "blur";
  /** `false` drops the plaque under the phone banner (Isaac, 2026-09-17, Sales:
   *  "on mobile remove artwork plaque and just have smooth gradient up into
   *  image"). The floating desktop plaque is unaffected. */
  mobilePlaque?: boolean;
}

export interface ReceiveItem {
  label: string;
  comingSoon?: boolean;
}

const DEFAULT_HEADING = "Every attendee walks away with more than just the workshop training:";

const ITEMS: ReceiveItem[] = [
  { label: "Lifetime access to the recording and chat transcript" },
  { label: "Two weeks of email support after the workshop" },
  { label: "The AdVenture Media Roger agent" },
  { label: "Lifetime access to AdVenture Media's Claude recipe generator tool" },
  { label: "Lifetime access to OnPrompt Studio ($799 value)" },
  { label: "Private Slack community for networking and troubleshooting", comingSoon: true },
];

/* Divider colors per Isaac's spec (charcoal/12 on light, cream/15 on dark).
   Exact alphas don't exist as tokens (nearest: --charcoal-14 / --cream-12),
   so the token base colors are inlined here at the requested opacity. */
const DIVIDER_ON_LIGHT = "hsla(60, 3%, 8%, 0.12)";
const DIVIDER_ON_DARK = "hsla(48, 20%, 92%, 0.15)";

function ComingSoonChip() {
  return (
    <span
      className="type-nav inline-block align-middle whitespace-nowrap rounded-[2px] px-2 py-0.5"
      style={{ backgroundColor: "var(--indigo)", color: "var(--cream)" }}
    >
      Coming soon
    </span>
  );
}

function ItemList({
  items,
  dark,
  drawn = false,
  className = "",
}: {
  items: ReceiveItem[];
  dark: boolean;
  /** Cowork design system: the drawn tick instead of the geometric one. */
  drawn?: boolean;
  className?: string;
}) {
  const divider = dark ? DIVIDER_ON_DARK : DIVIDER_ON_LIGHT;
  return (
    <ul className={`flex flex-col ${className}`}>
      {items.map((item, i) => (
        <li
          key={item.label}
          className="flex items-start gap-3 py-3.5"
          style={i > 0 ? { borderTop: `1px solid ${divider}` } : undefined}
        >
          {drawn ? (
            <span className="ar-tick shrink-0 -mt-[3px]" aria-hidden="true">
              <DsIcon name="check" size={26} />
            </span>
          ) : (
            <Check
              size={18}
              strokeWidth={2.5}
              aria-hidden="true"
              className="shrink-0 mt-[3px]"
              style={{ color: "var(--terra)" }}
            />
          )}
          <span className={`type-body ${dark ? "text-cream/90" : "text-charcoal"}`}>
            {item.label}
            {item.comingSoon && (
              <>
                {" "}
                <ComingSoonChip />
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReceiveContent({
  dark,
  twoCol = false,
  drawn = false,
  heading = DEFAULT_HEADING,
  items = ITEMS,
  cta = true,
}: {
  dark: boolean;
  twoCol?: boolean;
  drawn?: boolean;
  heading?: string;
  items?: ReceiveItem[];
  /** False drops the reserve button: the list stands alone (Sales, Isaac 2026-09-17). */
  cta?: boolean;
}) {
  /* Split at the halfway mark rather than at a hardcoded 3, so a list of four
     comes out 2 + 2 and the shipped list of six is still 3 + 3. */
  const split = Math.ceil(items.length / 2);
  return (
    <>
      <h2 className={`type-h3 mb-6 ${dark ? "text-cream" : "text-charcoal"}`}>
        {heading}
      </h2>
      {twoCol ? (
        <div className={`grid sm:grid-cols-2 gap-x-10 ${cta ? "mb-8" : ""}`}>
          <ItemList items={items.slice(0, split)} dark={dark} drawn={drawn} />
          <ItemList items={items.slice(split)} dark={dark} drawn={drawn} />
        </div>
      ) : (
        <ItemList items={items} dark={dark} drawn={drawn} className={cta ? "mb-8" : undefined} />
      )}
      {cta && (
        <button
          type="button"
          className="btn btn-cta btn-lg w-full sm:w-auto"
          onClick={() => requestCheckout()}
          data-testid="attendees-receive-reserve"
        >
          Reserve your seat
        </button>
      )}
    </>
  );
}

function DesktopSurface({
  variant,
  drawn,
  heading,
  items,
  cta,
}: {
  variant: AttendeesReceiveVariant;
  drawn: boolean;
  heading?: string;
  items?: ReceiveItem[];
  cta?: boolean;
}) {
  switch (variant) {
    case "card-light":
    case "card-dark": {
      const dark = variant === "card-dark";
      return (
        <div className="w-full max-w-[560px]">
          <div
            className="ar-card rounded-[4px] p-7 sm:p-9"
            style={{
              backgroundColor: dark ? "var(--charcoal)" : "var(--cream)",
              border: dark ? "1px solid var(--cream-12)" : "1px solid var(--charcoal)",
            }}
          >
            <ReceiveContent dark={dark} drawn={drawn} heading={heading} items={items} cta={cta} />
          </div>
        </div>
      );
    }
    case "card-two-col":
    // card-art is the SAME cream two-column card — only what sits behind it
    // changes (the painting replaces the grid + video), so it falls through.
    case "card-art":
      return (
        <div className="w-full max-w-[760px]">
          <div
            className="ar-card rounded-[4px] p-7 sm:p-9"
            style={{ backgroundColor: "var(--cream)", border: "1px solid var(--charcoal)" }}
          >
            <ReceiveContent dark={false} twoCol drawn={drawn} heading={heading} items={items} cta={cta} />
          </div>
        </div>
      );
  }
}

/** Museum wall label for the painting behind the card. A `<p>` so it stays a
 *  caption-level element wherever it is placed; the three spans carry the same
 *  words a one-line credit did, set the way a plaque sets them.
 *
 *  FROSTED GLASS, AND THIS IS THE HOUSE PLAQUE (Isaac, 2026-09-17: "claude
 *  monet plaque should appear frosted glass, and this becomes our standard
 *  plaque styling"). Any painting credit on the site from here on takes this
 *  treatment: a pale, translucent pane with a backdrop blur, one hairline of
 *  the same pale ink, 4px corners, no shadow, no double rule. On the painting
 *  (`floating`) the pane is cream glass with cream ink; under the mobile
 *  banner, where it sits on the band's own ground, the pane is the same glass
 *  tinted the other way, so it stays readable on paper.
 *
 *  Styled inline rather than through cowork-ds.css: this component ships to
 *  every workshop page and cowork-ds.css is only <link>ed on four of them, so a
 *  plaque that depended on it would be unstyled anywhere else that ever passes
 *  `art`. Desktop and mobile are separate renders (the band is `hidden
 *  lg:block`, the banner `lg:hidden`), so no media query is needed here. */
function ArtPlaque({ art, floating }: { art: AttendeesArt; floating: boolean }) {
  /* MOBILE INK IS A VARIABLE (2026-09-17). The non-floating plaque assumed the
     band under the phone banner was paper and inked itself teal-900. On the
     Sales page that band IS teal-900, so the label rendered at 1:1 contrast and
     disappeared. The ink now comes from --ar-plaque-ink, with the original
     literal as the fallback, so a page whose mobile band is dark sets the
     variable once (see `.ds-sales [aria-label="All attendees receive"]` in
     cowork-ds.css) and every page that does not is byte-identical. */
  const ink = floating ? "249, 249, 242" : "var(--ar-plaque-ink, 30, 61, 59)";
  return (
    <p
      className="ar-plaque"
      style={{
        margin: 0,
        ...(floating
          ? ({ position: "absolute", left: 24, bottom: 24, zIndex: 5 } as const)
          : null),
        // Narrower under the mobile banner, where a 300px label on a 342px
        // measure stops reading as a plaque and starts reading as a bar.
        maxWidth: floating ? 300 : "min(272px, 76vw)",
        padding: "12px 16px 13px",
        background: floating ? `rgba(${ink}, 0.14)` : `rgba(${ink}, 0.06)`,
        border: `1px solid rgba(${ink}, ${floating ? 0.34 : 0.22})`,
        WebkitBackdropFilter: "blur(16px) saturate(1.25)",
        backdropFilter: "blur(16px) saturate(1.25)",
        borderRadius: 4,
      }}
    >
      {art.artist && (
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-mono)",
            fontWeight: 500,
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: `rgba(${ink}, 0.72)`,
          }}
        >
          {art.artist}
        </span>
      )}
      {art.title && (
        <span
          style={{
            display: "block",
            marginTop: 5,
            fontFamily: "var(--font-display)",
            fontWeight: 400,
            fontSize: 16,
            lineHeight: 1.22,
            letterSpacing: "-0.01em",
            color: `rgb(${ink})`,
          }}
        >
          {art.title}
        </span>
      )}
      {art.year && (
        <span
          style={{
            display: "block",
            marginTop: 7,
            paddingTop: 7,
            borderTop: `1px solid rgba(${ink}, 0.22)`,
            fontFamily: "var(--font-mono)",
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: "0.12em",
            color: `rgba(${ink}, 0.66)`,
          }}
        >
          {art.year}
        </span>
      )}
    </p>
  );
}

/* The painting dissolves into the band ground on its right and bottom edges
   so the card sits on solid colour. Masks, not gradients on a fill: the fade
   is on the picture, the way the hero photo and the Design banner do it. */
const ART_FADE_MASK_DESKTOP =
  "linear-gradient(to right, #000 0%, #000 42%, rgba(0,0,0,0.55) 62%, transparent 86%)," +
  "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, #000 14%, #000 86%, rgba(0,0,0,0.6) 100%)";
/* Blur path: the plate hands off to a blurred copy of itself, not to bare
   ground, so the fade can start later and run longer without exposing
   anything. Sharp through the left third, then a slow dissolve. */
const ART_BLUR_HANDOFF_MASK_DESKTOP =
  "linear-gradient(to right, #000 0%, #000 34%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.45) 68%, rgba(0,0,0,0.12) 86%, transparent 100%)," +
  "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, #000 14%, #000 86%, rgba(0,0,0,0.6) 100%)";
/* Phone banner: a long, even fade so the cream ground rises into the painting
   rather than the painting stopping. Was opaque to 70%; it now starts letting
   go at 40% and eases out over the bottom three fifths. */
const ART_FADE_MASK_MOBILE =
  "linear-gradient(to bottom, #000 0%, #000 40%, rgba(0,0,0,0.78) 60%, rgba(0,0,0,0.4) 80%, transparent 100%)";

function ArtPlate({ art, mobile }: { art: AttendeesArt; mobile: boolean }) {
  const mask = mobile
    ? ART_FADE_MASK_MOBILE
    : art.tail === "blur" ? ART_BLUR_HANDOFF_MASK_DESKTOP : ART_FADE_MASK_DESKTOP;
  /* Both plates are in the DOM (lg:hidden / hidden lg:block). The mobile one
     is lazy, so a display:none box never fetches, but a 1px hint at desktop
     widths keeps any browser that does resolve it from picking the 1600w file. */
  const sizes = mobile ? "(min-width: 1024px) 1px, 100vw" : "(max-width: 1279px) 70vw, 62vw";
  const webpSet = `${art.base}-480.webp 480w, ${art.base}-800.webp 800w, ${art.base}-1200.webp 1200w, ${art.base}-1600.webp 1600w`;
  const blurTail = !mobile && art.tail === "blur";
  const plate = (
    <picture className={blurTail ? undefined : "ar-art"}>
      <source
        type="image/avif"
        srcSet={`${art.base}-480.avif 480w, ${art.base}-800.avif 800w, ${art.base}-1200.avif 1200w, ${art.base}-1600.avif 1600w`}
        sizes={sizes}
      />
      <img
        src={`${art.base}-1200.webp`}
        srcSet={`${art.base}-480.webp 480w, ${art.base}-800.webp 800w, ${art.base}-1200.webp 1200w, ${art.base}-1600.webp 1600w`}
        sizes={sizes}
        alt={art.alt ?? ""}
        width={art.width ?? 1600}
        height={art.height ?? 956}
        loading="lazy"
        decoding="async"
        className={mobile ? "w-full h-64 sm:h-80 object-cover block" : "absolute inset-y-0 left-0 h-full w-[68%] object-cover"}
        style={{
          objectPosition: art.position ?? "left center",
          WebkitMaskImage: mask,
          maskImage: mask,
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
        }}
      />
    </picture>
  );
  if (!blurTail) return plate;
  /* Blur: the soft copy fills the band and the sharp, masked plate lies on
     top. The soft copy is sized INLINE (width/height 100%, max-width none)
     rather than with `w-full`: cowork-ds.css caps `.w-full` inside this band
     at 1280px for the card rail, and that cap was cutting the painting off
     ~80px short of each edge on a 1440 screen, leaving bare ground beyond it.
     The 1.04 scale hides the blur's transparent fringe at the box edge; the
     slight darkening keeps a pale painting from reading as a wash under the
     card. `.ar-art` moves to the wrapper so the page rules that position the
     plate (cowork-ds.css) keep applying. */
  return (
    <div className="ar-art absolute inset-0" aria-hidden="true">
      {/* Same <source> as the sharp copy, or the browser takes the avif for
          one and the webp for the other and downloads the painting twice. */}
      <picture>
        <source
          type="image/avif"
          srcSet={`${art.base}-480.avif 480w, ${art.base}-800.avif 800w, ${art.base}-1200.avif 1200w, ${art.base}-1600.avif 1600w`}
          sizes={sizes}
        />
        <img
          src={`${art.base}-1200.webp`}
          srcSet={webpSet}
          sizes={sizes}
          alt=""
          width={art.width ?? 1600}
          height={art.height ?? 956}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 object-cover"
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "none",
            objectPosition: art.position ?? "left center",
            filter: "blur(22px) saturate(1.05) brightness(0.82)",
            transform: "scale(1.04)",
          }}
        />
      </picture>
      {plate}
    </div>
  );
}

/** Feathers the mobile banner's four edges (and the dark cabin block on the
 *  right) into transparency so it dissolves into the cream section instead of
 *  reading as a hard rectangle. Two masks intersected → an eased vignette. */
const BANNER_BLEND_MASK =
  "linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)," +
  "linear-gradient(to bottom, transparent 0%, #000 12%, #000 84%, transparent 100%)";

export function AttendeesReceive({
  variant = "card-light",
  blendImage = false,
  drawn = false,
  art,
  heading,
  items,
  cta = true,
}: {
  variant?: AttendeesReceiveVariant;
  /** Overrides the card's heading. Omit and every page keeps today's sentence. */
  heading?: string;
  /** Overrides the value list. Omit and every page keeps today's six items.
   *  Sales passes four: the Roger agent and the recipe generator are not part
   *  of that seat, and a list that promises them would be selling something the
   *  buyer does not get. */
  items?: ReceiveItem[];
  /** Design page: feather the mobile banner into the page (see mask above). */
  blendImage?: boolean;
  /** Cowork design system: drawn ticks in place of the lucide checks. */
  drawn?: boolean;
  /** `card-art` only: the painting behind the card. Omit it and the variant
   *  degrades to exactly the card-two-col rendering. */
  art?: AttendeesArt;
  /** False drops the reserve button; the list stands alone (Sales). */
  cta?: boolean;
}) {
  const isCard =
    variant === "card-light" ||
    variant === "card-dark" ||
    variant === "card-two-col" ||
    variant === "card-art";
  const darkContent = variant === "card-dark";
  // Mobile band surface: dark variants get a charcoal band, the rest cream.
  const mobileBandDark = darkContent;
  // card-art carries card-two-col's content shape (6 items, 3 + 3).
  const twoColContent = variant === "card-two-col" || variant === "card-art";
  /* The painting participates only when the variant asked for it AND one was
     actually supplied — this const is also what narrows `art` for the JSX. */
  const artPlate = variant === "card-art" ? art : undefined;
  /* A plaque only when the painting was actually named. */
  const hasPlaque = !!(artPlate && (artPlate.artist || artPlate.title || artPlate.year));

  // Perf: the <video> is desktop-only and lives well below the fold, but a
  // mounted <video autoplay> downloads immediately — even inside a
  // display:none container, so mobile was paying the ~1MB too. Mount it only
  // once (a) the viewport is lg+ AND (b) the band is within 600px of the
  // viewport. Until then the band is just charcoal + grid, and the poster
  // covers the brief load once it mounts.
  const bandRef = useRef<HTMLDivElement>(null);
  const [videoOn, setVideoOn] = useState(false);
  useEffect(() => {
    // card-art has no video — the painting is the surface. Nothing to observe.
    if (variant === "card-art") return;
    const el = bandRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && window.matchMedia("(min-width: 1024px)").matches) {
          setVideoOn(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [variant]);

  return (
    <section
      aria-label="All attendees receive"
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
    >
      {/* Mobile: short image banner, content stacked below. Responsive
          variants generated by asset-promote — serve the smallest that
          covers the viewport instead of the 193KB full-size webp. */}
      <div
        className="lg:hidden"
        style={blendImage ? { backgroundColor: "var(--charcoal)" } : undefined}
      >
        {/* Design page (blendImage): the band goes charcoal and the image's
            feathered edges dissolve into that black, so the dark cabin scene
            melts into the surface like the desktop treatment. */}
        {/* card-art: the painting stands in for the bot-home banner. */}
        {artPlate ? (
          <>
            <ArtPlate art={artPlate} mobile />
            {/* On a phone the painting is a banner across the top, so the
                plaque sits under it, left-aligned, instead of floating over
                it — a label pinned inside a 224px-tall banner would cover the
                picture it labels. */}
            {hasPlaque && artPlate.mobilePlaque !== false && (
              <div className="px-6 md:px-12 pt-5">
                <ArtPlaque art={artPlate} floating={false} />
              </div>
            )}
          </>
        ) : (
        <picture>
            <source
              type="image/avif"
              srcSet="/images/cowork-workshop/bot-home-hero-480.avif 480w, /images/cowork-workshop/bot-home-hero-800.avif 800w, /images/cowork-workshop/bot-home-hero-1200.avif 1200w"
              sizes="100vw"
            />
            <img
              src={BG_IMAGE}
              srcSet="/images/cowork-workshop/bot-home-hero-480.webp 480w, /images/cowork-workshop/bot-home-hero-800.webp 800w, /images/cowork-workshop/bot-home-hero-1200.webp 1200w"
              sizes="100vw"
              alt=""
              width={1200}
              height={675}
              loading="lazy"
              decoding="async"
              className="w-full h-56 sm:h-72 object-cover object-left block"
            style={
              blendImage
                ? {
                    objectPosition: "48% 50%",
                    WebkitMaskImage: BANNER_BLEND_MASK,
                    maskImage: BANNER_BLEND_MASK,
                    WebkitMaskComposite: "source-in",
                    maskComposite: "intersect",
                  }
                : undefined
            }
          />
        </picture>
        )}
        <div
          className="px-6 md:px-12 py-10"
          style={{ backgroundColor: mobileBandDark || blendImage ? "var(--charcoal)" : "var(--cream)" }}
        >
          {isCard ? (
            <div
              className="ar-card rounded-[4px] p-7 sm:p-9"
              style={{
                backgroundColor: darkContent ? "var(--charcoal)" : "var(--cream)",
                border: darkContent ? "1px solid var(--cream-12)" : "1px solid var(--charcoal)",
              }}
            >
              <ReceiveContent dark={darkContent} twoCol={twoColContent} drawn={drawn} heading={heading} items={items} cta={cta} />
            </div>
          ) : (
            <ReceiveContent dark={darkContent} drawn={drawn} heading={heading} items={items} cta={cta} />
          )}
        </div>
      </div>

      {/* Desktop: video lives IN the section as an ambient full-bleed
          surface — not a framed cinematic band (no aspect-ratio box; that
          read as a film-strip object sitting inside the section rather
          than being part of it). No left shift, no gradient patch — crop
          is centered and needs no patch. Stack, back to front:
            1. GridPattern, absolute inset-0 across the FULL charcoal
               band (padding included) — a section-wide surface, not just
               a patch over the video.
            2. Video, ALSO absolute inset-0 across the full band, in front
               of the grid: opacity 0.7 + filter: saturate(0.85-0.9) (light
               desaturation, not a wash-out) + transform: scale(0.7) from
               origin LEFT CENTER so the bot doesn't dominate the frame and
               more of the room reads (object-fit: cover on a very wide/
               short box zooms in hard; the scale-down compensates), while
               the video stays flush with the section's left edge — all the
               scale margin lands on the right, behind the card. Masked
               (VIDEO_FADE_MASK) so it dissolves into the charcoal — a
               short feather on the left (opaque by ~16%), the ellipse
               handling the top/right/bottom.
            3. Card, relative z-10, on top of both, in normal flow — its
               own height (plus py-28 above/below) is what gives the
               section its total height now that nothing is aspect-ratio-
               boxed.
          Video is the Veo loop (fire flicker + pipe smoke only), slowed 4x
          and web-optimized (1.8MB, silent, faststart) — unchanged source. */}
      <div
        ref={bandRef}
        className="hidden lg:block relative overflow-hidden py-28"
        style={{ backgroundColor: "var(--charcoal)" }}
      >
        {artPlate ? (
          /* card-art: the painting IS the surface — no grid, no video. Its
             masked right/bottom edges dissolve into the charcoal so the card
             lands on solid colour. */
          <ArtPlate art={artPlate} mobile={false} />
        ) : (
          <>
        {/* 0.05 opacity has no matching --cream-* structural-opacity token
            (lowest defined is --cream-07) — inlined per Isaac's explicit
            ask for cream-04/05; not a pattern to reuse elsewhere without a
            real reason to go below the token scale. */}
        <GridPattern
          width={24}
          height={24}
          x={-1}
          y={-1}
          className="absolute inset-0 z-0 fill-transparent stroke-[hsla(48,20%,92%,0.05)]"
        />
        {videoOn && (
          <video
            src="/videos/bot-home-loop.mp4"
            poster="/images/cowork-workshop/bot-home-hero-1600.webp"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{
              opacity: 0.25,
              filter: "saturate(0.85)",
              transform: "scale(0.7)",
              transformOrigin: "left center",
              maskImage: VIDEO_FADE_MASK,
              WebkitMaskImage: VIDEO_FADE_MASK,
              maskComposite: "intersect",
              WebkitMaskComposite: "source-in",
            }}
          />
        )}
          </>
        )}
        {/* The plaque hangs on the painting, not on the card's column — bottom
            left of the band, clear of the card on the right. A direct child of
            the relatively-positioned band, so its 24px inset is measured from
            the section edge rather than from the content container. */}
        {artPlate && hasPlaque && <ArtPlaque art={artPlate} floating />}
        <div className="relative z-10 container-content flex items-center justify-end">
          {artPlate ? (
            <div className="w-full max-w-[760px]">
              <DesktopSurface variant={variant} drawn={drawn} heading={heading} items={items} cta={cta} />
            </div>
          ) : (
            <DesktopSurface variant={variant} drawn={drawn} heading={heading} items={items} cta={cta} />
          )}
        </div>
      </div>
    </section>
  );
}
